/**
 * Copyright 2021 Design Barn Inc.
 */

import classNames from 'classnames';
import type { ForwardedRef } from 'react';
import React from 'react';

import Spinner from './spinner';

interface ButtonProps {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  id?: string;
  isBlock?: boolean;
  isTight?: boolean;
  loading?: boolean;
  name?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  size?: 'sm' | 'md' | 'lg';
  type?: string;
  value?: string;
  variant?: 'primary' | 'outline' | 'ghost' | 'ghost-danger' | 'always-loading';
}

export const Button = React.forwardRef(
  (
    {
      children,
      className,
      disabled,
      id,
      isBlock = true,
      isTight = false,
      loading = false,
      name,
      onClick,
      size = 'md',
      type,
      value,
      variant = 'primary',
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ): React.ReactElement => {
    const classes = classNames(className, 'font-semibold text-sm text-center rounded-md', {
      'w-full': isBlock,
      'bg-teal-300 text-white hover:bg-teal-400': variant === 'primary',
      'bg-white border border-gray-700 text-gray-700': variant === 'outline',
      'opacity-30 cursor-not-allowed': disabled,
      'bg-white text-teal-300 hover:text-teal-400 font-normal': variant === 'ghost',
      'bg-white text-red-500 font-normal': variant === 'ghost-danger',
      'text-sm h-[46px] px-6': size === 'md',
      'text-base h-[56px] px-6': size === 'lg',
      'text-xs py-2 px-4': size === 'sm',
      'px-0 py-0 h-auto': isTight,
      'text-teal-500 cursor-not-allowed opacity-100': loading,
      'bg-teal-300 text-white px-2 cursor-not-allowed': variant === 'always-loading',
    });

    let content = children;

    if (loading || variant === 'always-loading') {
      content = (
        <div className="flex items-center justify-center">
          <Spinner className="mr-2" />
          {children}
        </div>
      );
    }

    // eslint yells when `type` attribute being passed to prop to button element.
    switch (type) {
      case 'submit':
        return (
          <button
            ref={ref}
            type="submit"
            className={classes}
            onClick={onClick}
            disabled={disabled}
            name={name}
            value={value}
            id={id}
          >
            {content}
          </button>
        );

      default:
        return (
          <button
            ref={ref}
            type="button"
            className={classes}
            onClick={onClick}
            disabled={disabled}
            name={name}
            value={value}
            id={id}
          >
            {content}
          </button>
        );
    }
  },
);
